import { useEffect, useState, useRef } from "react";
import { AppLayout } from "@/components/AppLayout";
import { api, getToken, API, formatApiErrorDetail } from "@/lib/api";
import { formatRupiah, MONTHS } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Receipt, UploadSimple, Image as ImageIcon } from "@phosphor-icons/react";
import { ReimbursementDetailModal } from "@/components/DetailModals";
import { ReimbursementStatusPill } from "@/components/PaymentStatusPill";
import { DeleteConfirmButton } from "@/components/DeleteConfirmButton";
import { CurrencyInput, DatePicker, MonthYearPicker } from "@/components/inputs";

export default function ReimbursementPage() {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const periodYears = [now.getFullYear() - 2, now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1];
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = () => {
    api.get(`/reimbursements/me?month=${month}&year=${year}`).then((res) => setItems(res.data));
  };
  useEffect(() => { load(); }, [month, year]);

  const total = items.reduce((s, i) => s + Number(i.amount || 0), 0);
  const paid = items.filter((i) => i.status === "paid").reduce((s, i) => s + Number(i.amount || 0), 0);

  const remove = async (id) => {
    await api.delete(`/reimbursements/${id}`);
    toast.success("Data dihapus");
    load();
  };

  return (
    <AppLayout title="Reimbursement">
      <div className="max-w-5xl space-y-6 animate-fade-up">
        {/* Part 1: Summary */}
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="flex items-end gap-3">
            <div className="space-y-1.5">
              <Label>Periode Bulan</Label>
              <MonthYearPicker month={month} year={year} onChange={(m, y) => { setMonth(m); setYear(y); }} years={periodYears} monthTestId="reimbursement-month-select" yearTestId="reimbursement-year-select" className="w-64" />
            </div>
          </div>
          <AddDialog open={open} setOpen={setOpen} month={month} year={year} onDone={load} />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card className="p-5">
            <p className="text-sm font-medium text-muted-foreground">Total Klaim</p>
            <p className="font-heading text-2xl font-bold mt-1 tabular-nums" data-testid="reimbursement-total">{formatRupiah(total)}</p>
          </Card>
          <Card className="p-5">
            <p className="text-sm font-medium text-muted-foreground">Sudah Dibayar</p>
            <p className="font-heading text-2xl font-bold mt-1 tabular-nums text-[hsl(var(--success))]">{formatRupiah(paid)}</p>
          </Card>
          <Card className="p-5">
            <p className="text-sm font-medium text-muted-foreground">Belum Dibayar</p>
            <p className="font-heading text-2xl font-bold mt-1 tabular-nums text-[hsl(var(--warning))]">{formatRupiah(total - paid)}</p>
          </Card>
        </div>

        {/* Part 2: Details table */}
        <Card className="overflow-hidden overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-16">No</TableHead>
                <TableHead>Tanggal</TableHead>
                <TableHead>Keterangan</TableHead>
                <TableHead className="text-right">Jumlah</TableHead>
                <TableHead>Bon</TableHead>
                <TableHead>Status</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-12 text-muted-foreground">
                    <Receipt size={36} weight="duotone" className="mx-auto mb-2 opacity-40" />
                    Belum ada reimbursement di periode ini.
                  </TableCell>
                </TableRow>
              ) : (
                items.map((r, idx) => (
                  <TableRow key={r.id} className="cursor-pointer" onClick={() => setDetail(r)} data-testid={`reimbursement-row-${idx}`}>
                    <TableCell>{r.no}</TableCell>
                    <TableCell>{r.date}</TableCell>
                    <TableCell className="max-w-xs truncate">{r.description}</TableCell>
                    <TableCell className="text-right tabular-nums font-medium">{formatRupiah(r.amount)}</TableCell>
                    <TableCell>
                      {r.photo_path ? (
                        <Button
                          variant="link"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); setPreview(`${API}/files/${r.photo_path}?auth=${getToken()}`); }}
                          aria-label={`Lihat foto bon reimbursement ${r.no || ""}`}
                          data-testid={`view-receipt-${idx}`}
                        >
                          <ImageIcon size={16} weight="bold" /> Lihat
                        </Button>
                      ) : (
                        <span className="text-muted-foreground text-xs">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <ReimbursementStatusPill status={r.status} />
                    </TableCell>
                    <TableCell>
                      <DeleteConfirmButton
                        title="Hapus reimbursement?"
                        description="Data reimbursement dan foto bon akan dihapus permanen."
                        onConfirm={() => remove(r.id)}
                        triggerTestId={`delete-reimbursement-${idx}`}
                        confirmTestId={`confirm-delete-reimbursement-${idx}`}
                        stopPropagation
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Card>
      </div>

      <Dialog open={!!preview} onOpenChange={() => setPreview(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Foto Bon</DialogTitle></DialogHeader>
          {preview && <img src={preview} alt="Bon" className="w-full max-h-[70vh] rounded-lg border border-border bg-muted object-contain" />}
        </DialogContent>
      </Dialog>
      <ReimbursementDetailModal item={detail} onClose={() => setDetail(null)} />
    </AppLayout>
  );
}

function AddDialog({ open, setOpen, month, year, onDone }) {
  const fileRef = useRef();
  const [form, setForm] = useState({ amount: "", description: "", date: "" });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setForm({ amount: "", description: "", date: "" });
    setFile(null);
  };

  const submit = async () => {
    if (!form.date || !form.amount) {
      toast.error("Lengkapi Tanggal dan Jumlah");
      return;
    }
    setSaving(true);
    try {
      let photo_path = null;
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        const up = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        photo_path = up.data.path;
      }
      const [, mm, yy] = form.date.split("/").map(Number);
      await api.post("/reimbursements", {
        date: form.date,
        amount: Number(form.amount),
        description: form.description,
        photo_path,
        month: mm,
        year: yy,
      });
      toast.success("Reimbursement ditambahkan");
      reset();
      setOpen(false);
      onDone();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="gap-2" data-testid="add-reimbursement-button">
          <Plus size={18} weight="bold" /> Tambah Reimbursement
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Tambah Reimbursement</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Tanggal</Label>
            <DatePicker value={form.date} onChange={(v) => setForm({ ...form, date: v })} testId="reimbursement-date-picker" />
          </div>
          <div className="space-y-1.5">
            <Label>Jumlah (Rp)</Label>
            <CurrencyInput value={form.amount} onChange={(num) => setForm({ ...form, amount: num })} data-testid="reimbursement-amount-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Keterangan</Label>
            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Contoh: Transport meeting klien" data-testid="reimbursement-description-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Foto Bon</Label>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => setFile(e.target.files[0])} data-testid="reimbursement-file-input" />
            <Button variant="outline" className="w-full h-10 gap-2.5 justify-start px-3.5" onClick={() => fileRef.current?.click()}>
              <UploadSimple size={18} /> {file ? file.name : "Upload foto bon"}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">Nomor reimbursement dibuat otomatis oleh sistem.</p>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={saving} data-testid="submit-reimbursement-button">
            {saving ? "Menyimpan…" : "Simpan"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
